import type { NotificationDeliverySelectionInput } from '../types/notification-delivery-selection.types';

/** Removes unsupported and duplicate choices without changing their order. */
export const normalizeDeliverySelection = <Channel extends string>(
  values: string[],
  available: Channel[]
): Channel[] =>
  Array.from(
    new Set(
      values.filter((value): value is Channel =>
        available.some((channel) => channel === value)
      )
    )
  );

/** Resolves the existing settings fallback and phone-channel restriction rules. */
export const resolveNotificationDeliverySelection = <Channel extends string>({
  available,
  defaults,
  selected,
  all,
  fallback,
  restricted,
}: NotificationDeliverySelectionInput<Channel>) => {
  const availableDeliveryChannels = normalizeDeliverySelection(
    available?.length ? available : defaults?.length ? defaults : fallback,
    all
  );
  const resolvedAvailable =
    availableDeliveryChannels.length > 0
      ? availableDeliveryChannels
      : [...fallback];
  const defaultDeliveryChannels = normalizeDeliverySelection(
    defaults?.length ? defaults : resolvedAvailable,
    resolvedAvailable
  );
  const resolvedDefault =
    defaultDeliveryChannels.length > 0
      ? defaultDeliveryChannels
      : resolvedAvailable;
  const selectedDeliveryChannels = normalizeDeliverySelection(
    selected ?? resolvedDefault,
    resolvedAvailable
  );
  const effectiveDefault = resolvedDefault.filter(
    (channel) => !restricted.includes(channel)
  );
  const sanitizedSelection = selectedDeliveryChannels.filter(
    (channel) => !restricted.includes(channel)
  );
  return {
    resolvedAvailable,
    effectiveDefault,
    deliveryChannelsValue:
      sanitizedSelection.length > 0 ? sanitizedSelection : effectiveDefault,
  };
};
